import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { message, ConfigProvider } from 'antd';
import { Button, Space, Typography, Divider } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faTrash, faBullseye } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { useAuth } from '../../../hooks/useAuth';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import type { AppDispatch, RootState } from '../../../redux/store';
import { fetchPerformanceComparison } from './store/itemPerformanceComparisonSlice';
import PerformanceSummary from './PerformanceSummary';
import PeriodComparisonCard from './PeriodComparisonCard';
import PeriodComparisonTable from './PeriodComparisonTable';
import StoresDropDown from '../StoresDropDown';
import {
  Container,
  ContentWrapper,
  HeaderSection,
  HeaderContent,
  StyledCard,
  ConfigSection,
  FormRow,
  PeriodsSection,
  PeriodHeader,
  PeriodFields,
  ActionButton,
  StyledButton,
  StyledInput,
  StyledDateInput,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  BackButtonWrapper,
  TitleNoMargin,
  LabelText,
  SecondaryLabelText,
  PeriodTitleText,
  PeriodCardWrapper,
  ActionButtonWrapper,
  ComparisonTitleWrapper,
  ComparisonCardWrapper,
  MobileSpace,
} from './styles';
import { DEFAULT_PERIOD, DATE_FORMAT } from './constants';
import type { Period } from './types';

const { Text } = Typography;

const ItemPerformanceComparison: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();

  const merchant = user as { id?: string; role?: string; storeId?: string } | null;
  const storeId = (user as { storeId?: string })?.storeId || null;

  const { performanceData, loading, error } = useSelector(
    (state: RootState) => state.itemPerformanceComparison
  );

  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<{ id: string } | null>(null);
  const [periods, setPeriods] = useState<Period[]>([DEFAULT_PERIOD]);

  const themeMode: 'light' | 'dark' = isDarkMode ? 'dark' : 'light';
  const styledTheme = { mode: themeMode } as DefaultTheme;

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleItemIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedItemId(e.target.value);
  };

  const handleStoreChange = (store: { id: string } | null) => {
    if (store && store.id) {
      setSelectedStore(store);
    } else {
      setSelectedStore(null);
    }
  };

  const addPeriod = () => {
    const newPeriod: Period = {
      startDate: moment().subtract(60, 'days').toDate(),
      endDate: moment().subtract(30, 'days').toDate(),
      label: `Period ${periods.length + 1}`,
    };
    setPeriods([...periods, newPeriod]);
  };

  const removePeriod = (index: number) => {
    if (periods.length > 1) {
      setPeriods(periods.filter((_, i) => i !== index));
    }
  };

  const updatePeriod = (
    index: number,
    field: keyof Period,
    value: Date | string
  ) => {
    const updatedPeriods = [...periods];
    updatedPeriods[index] = { ...updatedPeriods[index], [field]: value };
    setPeriods(updatedPeriods);
  };

  const handleCompare = () => {
    if (!selectedItemId) {
      message.warning('Please enter an item ID to compare performance');
      return;
    }

    if (!merchant?.id) {
      message.error('Merchant ID is required');
      return;
    }

    const periodsParam = periods.map((period) => ({
      startDate: moment(period.startDate).format('YYYY-MM-DD'),
      endDate: moment(period.endDate).format('YYYY-MM-DD'),
      label: period.label,
    }));

    dispatch(
      fetchPerformanceComparison({
        itemId: selectedItemId,
        periods: periodsParam,
        merchantId: merchant.id,
        store: selectedStore?.id || storeId || null,
      })
    );
  };

  const isMobile = window.innerWidth < 768;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <ContentWrapper>
          <HeaderSection>
            <BackButtonWrapper>
              <Button
                icon={<FontAwesomeIcon icon={faArrowLeft} />}
                onClick={() => navigate(-1)}
                shape="circle"
              />
            </BackButtonWrapper>
            <HeaderContent>
              <TitleNoMargin level={3}>
                Item Performance Comparison
              </TitleNoMargin>
              <Text type="secondary">
                Compare item performance across different time periods
              </Text>
            </HeaderContent>
          </HeaderSection>

          <StyledCard title="Configuration">
            <ConfigSection>
              <FormRow>
                <div>
                  <LabelText strong>Item ID</LabelText>
                  <StyledInput
                    placeholder="Enter item ID (e.g., 123)"
                    value={selectedItemId}
                    onChange={handleItemIdChange}
                    size="large"
                  />
                </div>

                <div>
                  <LabelText strong>Store</LabelText>
                  <StoresDropDown
                    onOptionSelect={handleStoreChange}
                    merchantId={merchant?.id}
                    role={merchant?.role}
                  />
                </div>
              </FormRow>

              <Divider />

              <PeriodsSection>
                <PeriodHeader>
                  <Text strong>Comparison Periods</Text>
                  <StyledButton
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={addPeriod}
                    size="small"
                    type="dashed"
                  >
                    Add Period
                  </StyledButton>
                </PeriodHeader>

                {periods.map((period, index) => (
                  <PeriodCardWrapper
                    key={index}
                    size="small"
                    extra={
                      periods.length > 1 ? (
                        <StyledButton
                          icon={<FontAwesomeIcon icon={faTrash} />}
                          onClick={() => removePeriod(index)}
                          type="text"
                          danger
                          size="small"
                        >
                          Remove
                        </StyledButton>
                      ) : null
                    }
                  >
                    <PeriodTitleText strong>
                      Period {index + 1}
                    </PeriodTitleText>
                    <PeriodFields>
                      <div>
                        <SecondaryLabelText type="secondary">
                          Start Date
                        </SecondaryLabelText>
                        <StyledDateInput
                          type="date"
                          value={moment(period.startDate).format(DATE_FORMAT)}
                          onChange={(e) =>
                            updatePeriod(index, 'startDate', new Date(e.target.value))
                          }
                          size="small"
                        />
                      </div>
                      <div>
                        <SecondaryLabelText type="secondary">
                          End Date
                        </SecondaryLabelText>
                        <StyledDateInput
                          type="date"
                          value={moment(period.endDate).format(DATE_FORMAT)}
                          onChange={(e) =>
                            updatePeriod(index, 'endDate', new Date(e.target.value))
                          }
                          size="small"
                        />
                      </div>
                      <div>
                        <SecondaryLabelText type="secondary">
                          Label
                        </SecondaryLabelText>
                        <StyledInput
                          placeholder="Period label"
                          value={period.label}
                          onChange={(e) =>
                            updatePeriod(index, 'label', e.target.value)
                          }
                          size="small"
                        />
                      </div>
                    </PeriodFields>
                  </PeriodCardWrapper>
                ))}
              </PeriodsSection>

              <ActionButtonWrapper>
                <ActionButton
                  type="primary"
                  icon={<FontAwesomeIcon icon={faBullseye} />}
                  onClick={handleCompare}
                  loading={loading}
                  disabled={!selectedItemId}
                  size="large"
                >
                  Compare Performance
                </ActionButton>
              </ActionButtonWrapper>
            </ConfigSection>
          </StyledCard>

          {performanceData && (
            <div>
              <PerformanceSummary
                summary={performanceData.summary}
                borderColor="#1890ff"
              />
              <ComparisonCardWrapper
                title={
                  <ComparisonTitleWrapper>
                    <span>Period Comparisons</span>
                    <Text type="secondary">Item ID: {performanceData.itemId}</Text>
                  </ComparisonTitleWrapper>
                }
              >
                {isMobile ? (
                  <MobileSpace>
                    <Space direction="vertical" size="middle">
                      {performanceData.comparisons.map((comparison, index) => (
                        <PeriodComparisonCard
                          key={index}
                          comparison={comparison}
                          index={index}
                        />
                      ))}
                    </Space>
                  </MobileSpace>
                ) : (
                  <PeriodComparisonTable
                    comparisons={performanceData.comparisons}
                    isLoading={loading}
                  />
                )}
              </ComparisonCardWrapper>
            </div>
          )}

          {!performanceData && !loading && selectedItemId && (
            <StyledCard>
              <EmptyStateContainer>
                <EmptyStateIcon>
                  <FontAwesomeIcon icon={faBullseye} />
                </EmptyStateIcon>
                <EmptyStateTitle level={4}>Ready to Compare</EmptyStateTitle>
                <EmptyStateText>
                  Click "Compare Performance" to analyze item performance across
                  selected periods
                </EmptyStateText>
              </EmptyStateContainer>
            </StyledCard>
          )}
          </ContentWrapper>
        </Container>
      </ConfigProvider>
    </StyledThemeProvider>
  );
};

export default ItemPerformanceComparison;
